/**
 * Barre latérale PAR NIVEAU — lot U6b, vague 1 (SRF-TRV-052 ; § 4.3, § 4.9 ; planches `Nav`,
 * `BarreLaterale`). Un seul composant pour les quatre niveaux (Plateforme, Organisation,
 * Programme, Projet) ; le niveau vient de la coquille (`useNiveau`).
 *
 * DEUX niveaux imbriqués et REPLIABLES (décisions de l'utilisateur, 2026-09-27 puis 2026-09-30) :
 * des GROUPES (niveau 1) et, dans un groupe, ses ÉTAPES (« ① Cadrage », niveau 2) qui se
 * déplient et se replient à leur tour ; les entrées sont les liens de l'étape. Une entrée seule
 * (Vue projet, Réglages, la plupart des entrées d'organisation) est un lien de premier niveau ;
 * les entrées sans étape restent des liens directs du groupe. L'en-tête d'étape n'est PAS
 * collant : un bouton collant recouvre les liens qui défilent sous lui (axe `target-size`,
 * WCAG 2.5.8, mesuré le 2026-09-30) ; replier l'étape remplace le repère collant d'UX-2-01.
 *
 * - En-tête de groupe = BOUTON, `aria-expanded` exact, `aria-controls` vers sa liste (toujours
 *   montée, `hidden` quand repliée), le focus reste sur l'en-tête après bascule (A11Y-09).
 * - Le groupe de la page courante s'ouvre en y arrivant ; le repli est mémorisé par
 *   utilisateur et par niveau (N27, `localStorage` sous try/catch).
 * - Même contrat pour l'en-tête d'étape : bouton, `aria-expanded`, `aria-controls`, repli
 *   mémorisé (espace `etapes`), étape de la page courante ouverte à l'arrivée, notifications
 *   d'une étape repliée remontées sur son en-tête.
 * - Clavier : Entrée / Espace basculent (bouton natif) ; flèches haut/bas, Début/Fin déplacent
 *   le focus entre les contrôles visibles de la barre.
 * - Entrée 0.5.x : marque visible « 0.5.x » + « conçu, livré après la v0.5.0 » pour le lecteur
 *   d'écran. Entrée réservée : cadenas + « réservé à l'administration », montré à qui la consulte
 *   sans l'administrer (jamais à l'administration de l'organisation ni de la plateforme).
 *
 * Contrats préservés : badges de notifications par `navKey` (`NotificationVisibility`),
 * version et langue au pied, vue par profil, emplacement du quota.
 */
import { useCallback, useEffect, useRef, useState, type JSX, type KeyboardEvent, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowUp, Bell, ChevronDown, CircleHelp, Clock3, Lock, Search, X } from 'lucide-react'

import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarRail, useSidebar } from '@/components/ui/sidebar'
import {
  ACCUEIL, MARQUE_0_5_X, currentNavItem, itemsDuGroupePourMode, itemVisiblePourRoles, niveauParId,
  premierNiveau, sectionDuChemin, type Niveau, type NavGroup, type NavItem,
} from '@/lib/nav'
import type { NavigationMode } from '@/lib/navigationMode'
import { api } from '@/api/client'
import { APP_VERSION, BUILD_DATE } from '@/lib/version'
import { useT } from '@/i18n/language'
import { Brand } from '@/components/Brand'
import { SelecteurVue } from '@/components/profil/SelecteurVue'
import { useWorkspace } from '@/hooks/useWorkspace'
import type { Session } from '@/auth/session'
import { CreateMenu } from '@/components/CreateMenu'
import { libelleSousTitreEtape } from '@/lib/vueProjetTexte'

const CLE_REPLIS = 'doit-nav-replis'

/** Clé de mémoire du repli : par utilisateur ET par niveau (N27) ; `espace` sépare les étapes. */
export function cleReplis(utilisateur: string, niveau: Niveau, espace?: 'etapes'): string {
  return `${CLE_REPLIS}:${utilisateur || 'anonyme'}:${niveau}${espace ? `:${espace}` : ''}`
}

/** Identifiant stable d'une étape dans son groupe (mémoire du repli, tests) : jamais traduit. */
export function idEtape(groupe: string, etape: string): string {
  return `${groupe}:${etape}`
}

/** Entrées d'un groupe regroupées par étape, dans l'ordre de première apparition. */
export function blocsParEtape(items: NavItem[]): { etape?: string; items: NavItem[] }[] {
  const blocs: { etape?: string; items: NavItem[] }[] = []
  const parEtape = new Map<string, { etape?: string; items: NavItem[] }>()
  for (const item of items) {
    if (!item.etape) { blocs.push({ items: [item] }); continue }
    let bloc = parEtape.get(item.etape)
    if (!bloc) {
      bloc = { etape: item.etape, items: [] }
      parEtape.set(item.etape, bloc)
      blocs.push(bloc)
    }
    bloc.items.push(item)
  }
  return blocs
}

export function lireReplis(cle: string): Set<string> {
  try {
    const brut = localStorage.getItem(cle)
    const valeur: unknown = brut ? JSON.parse(brut) : []
    return new Set(Array.isArray(valeur) ? valeur.filter((v): v is string => typeof v === 'string') : [])
  } catch {
    return new Set()
  }
}

function ecrireReplis(cle: string, replis: Set<string>): void {
  try { localStorage.setItem(cle, JSON.stringify([...replis])) } catch { /* stockage indisponible : mémoire de session seulement */ }
}

/** Mémoire du repli des groupes d'un niveau ; le groupe de la page courante s'ouvre à l'arrivée. */
function useReplis(utilisateur: string, niveau: Niveau, groupeCourant: string | undefined, groupeADeplier?: string,
  espace?: 'etapes'): {
  estReplie: (id: string) => boolean
  basculer: (id: string) => void
} {
  const cle = cleReplis(utilisateur, niveau, espace)
  const [etat, setEtat] = useState<{ cle: string; replis: Set<string> }>(() => ({ cle, replis: lireReplis(cle) }))
  // Changement de niveau ou d'utilisateur : relire la mémoire de ce niveau.
  const replis = etat.cle === cle ? etat.replis : lireReplis(cle)
  if (etat.cle !== cle) setEtat({ cle, replis })

  // Le groupe de la page courante s'ouvre EN Y ARRIVANT (pas à chaque rendu : un repli manuel
  // du groupe courant reste respecté ensuite).
  useEffect(() => {
    if (!groupeCourant) return
    setEtat((avant) => {
      const base = avant.cle === cle ? avant.replis : lireReplis(cle)
      if (!base.has(groupeCourant)) return avant.cle === cle ? avant : { cle, replis: base }
      const suivant = new Set(base)
      suivant.delete(groupeCourant)
      ecrireReplis(cle, suivant)
      return { cle, replis: suivant }
    })
  }, [cle, groupeCourant])

  // U6b vague 9 : le rail tablette ouvre le panneau sur le groupe touché (NavigationTablette).
  useEffect(() => {
    if (!groupeADeplier) return
    setEtat((avant) => {
      const base = avant.cle === cle ? avant.replis : lireReplis(cle)
      if (!base.has(groupeADeplier)) return avant.cle === cle ? avant : { cle, replis: base }
      const suivant = new Set(base)
      suivant.delete(groupeADeplier)
      ecrireReplis(cle, suivant)
      return { cle, replis: suivant }
    })
  }, [cle, groupeADeplier])

  const basculer = useCallback((id: string) => {
    setEtat((avant) => {
      const suivant = new Set(avant.cle === cle ? avant.replis : lireReplis(cle))
      if (suivant.has(id)) suivant.delete(id)
      else suivant.add(id)
      ecrireReplis(cle, suivant)
      return { cle, replis: suivant }
    })
  }, [cle])

  return { estReplie: (id) => replis.has(id), basculer }
}

/** Flèches haut/bas, Début/Fin : focus entre les liens et en-têtes VISIBLES de la barre. */
function deplacerFocus(event: KeyboardEvent<HTMLElement>): void {
  const touches = ['ArrowDown', 'ArrowUp', 'Home', 'End']
  if (!touches.includes(event.key)) return
  const cibles = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('a[href], button'))
    .filter((el) => el.closest('[hidden]') === null)
  const i = cibles.indexOf(document.activeElement as HTMLElement)
  if (i < 0 || cibles.length === 0) return
  event.preventDefault()
  const n = cibles.length
  const suivant = event.key === 'Home' ? 0
    : event.key === 'End' ? n - 1
      : event.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n
  cibles[suivant]?.focus()
}

const LIEN = 'flex min-h-11 w-full min-w-0 items-center gap-2 rounded-md px-2 text-left text-sm outline-none '
  + 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring '
  + 'aria-[current=page]:bg-sidebar-accent aria-[current=page]:font-semibold aria-[current=page]:text-sidebar-accent-foreground'

function Badge({ n, testid, libelle }: { n: number; testid: string; libelle: string }): JSX.Element | null {
  if (n <= 0) return null
  return (
    <span data-testid={testid}
          className="ml-auto flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold tabular-nums text-primary-foreground">
      <span aria-hidden="true">{n > 99 ? '99+' : n}</span>
      <span className="sr-only">{libelle}</span>
    </span>
  )
}

/**
 * « ↑ Niveau … » (§ 4.9) : le niveau au-dessus du courant. Lot NAV 0.5.x (A2 constat 1) : rendu à
 * TOUTES les tailles — en bureau, la barre de contexte n'est pas montée et ce lien était le seul
 * chemin vers le niveau Programme ; il n'existait qu'au tiroir téléphone.
 */
const NIVEAU_PARENT: Partial<Record<Niveau, { to: string; label: string }>> = {
  projet: { to: '/programme', label: 'Niveau programme' },
  programme: { to: '/tableau-de-bord', label: 'Niveau organisation' },
}

export function AppSidebar({ session, mode, niveau = 'projet', onModeChange, slotPied, groupeADeplier, onRechercher, onNewProject }: {
  session: Session
  mode: NavigationMode
  /** Niveau de la coquille ; défaut Projet (tests et montages sans coquille). */
  niveau?: Niveau
  /** Bascule Complet / Découverte au pied (niveau Projet). Absente : la bascule n'est pas rendue. */
  onModeChange?: (mode: NavigationMode) => void
  /** Emplacement du pied réservé au badge de quota courant. Vide = rien de rendu. */
  slotPied?: ReactNode
  /** Groupe à déplier à l'ouverture du panneau (rail tablette). */
  groupeADeplier?: string | undefined
  /** Téléphone : « Rechercher un écran, une action… » en tête du tiroir (palette ⌘K). */
  onRechercher?: (() => void) | undefined
  /** Téléphone : « Créer… » au pied du tiroir (la barre haute n'a plus la place). */
  onNewProject?: (() => void) | undefined
}): JSX.Element {
  const { pathname, search } = useLocation()
  const { isMobile, variante, openMobile, setOpenMobile } = useSidebar()
  const telephone = variante === 'telephone'
  const { t, lang, setLang } = useT()
  const { activeId: workspaceId } = useWorkspace()
  const actif = currentNavItem(pathname, search)
  const groupeCourant = sectionDuChemin(pathname, search)
  const niv = niveauParId(niveau)
  const replis = useReplis(session.username, niveau, groupeCourant, groupeADeplier)
  // U6b vague 9 : dans le tiroir (téléphone) et le panneau (tablette), accordéon qui s'ouvre sur
  // le groupe courant — ou celui touché dans le rail — les autres repliés (NavigationTelephone,
  // NavigationTablette). Non mémorisé : la mémoire du repli (N27) reste celle de la barre bureau.
  const [ouvertsPanneau, setOuvertsPanneau] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    if (!openMobile) return
    setOuvertsPanneau(new Set([groupeADeplier ?? groupeCourant].filter((g): g is string => Boolean(g))))
  }, [openMobile, groupeCourant, groupeADeplier])
  // Bureau (planche BarreLaterale) : sur une route d'un groupe, seul ce groupe est déplié d'office ; les
  // autres restent repliés (hors groupe — Accueil, Vue projet — tout reste déplié, N27) tant qu'on ne les ouvre pas (ouverture de la session, non mémorisée).
  // Un repli explicite (N27) reste mémorisé comme avant.
  const [ouvertsBureau, setOuvertsBureau] = useState<Set<string>>(() => new Set())
  const groupeDeLaRoute = actif
    ? premierNiveau(niveau, session.roles, mode).find((g) => g.type !== 'entree' && g.items.includes(actif))?.id
    : undefined
  // Niveau 2 : l'étape de la page courante s'ouvre en y arrivant ; les autres gardent leur repli.
  const etapeCourante = groupeDeLaRoute && actif?.etape ? idEtape(groupeDeLaRoute, actif.etape) : undefined
  const replisEtapes = useReplis(session.username, niveau, etapeCourante, undefined, 'etapes')
  const estReplie = (id: string): boolean => isMobile
    ? !ouvertsPanneau.has(id)
    : replis.estReplie(id) || (groupeDeLaRoute !== undefined && id !== groupeDeLaRoute && !ouvertsBureau.has(id))
  const basculer = (id: string): void => {
    if (!isMobile) {
      if (estReplie(id) && !replis.estReplie(id)) {
        setOuvertsBureau((avant) => new Set(avant).add(id))
        return
      }
      if (!estReplie(id)) setOuvertsBureau((avant) => { const suivant = new Set(avant); suivant.delete(id); return suivant })
      replis.basculer(id)
      if (replis.estReplie(id)) setOuvertsBureau((avant) => new Set(avant).add(id))
      return
    }
    setOuvertsPanneau((avant) => {
      const suivant = new Set(avant)
      if (suivant.has(id)) suivant.delete(id)
      else suivant.add(id)
      return suivant
    })
  }

  // Badges de notifications non lues par destination (contrat `NotificationVisibility`).
  const nonLuesParDestination = useQuery({
    queryKey: ['notifications-unread-by-nav', workspaceId],
    queryFn: () => api.unreadNotificationsByNav(workspaceId!),
    enabled: Boolean(workspaceId),
    refetchInterval: 20000,
    refetchIntervalInBackground: false,
  })
  // Même clé de cache que la barre haute : le tiroir et la barre ne peuvent pas diverger.
  const nonLues = useQuery({
    queryKey: ['notifications-unread', workspaceId],
    queryFn: () => api.unreadNotificationsCount(workspaceId!),
    enabled: Boolean(workspaceId) && telephone,
    refetchInterval: 20000,
    refetchIntervalInBackground: false,
  })
  const nNonLues = nonLues.data?.count ?? 0
  const parent = NIVEAU_PARENT[niveau]
  const compteurs = new Map((nonLuesParDestination.data?.items ?? [])
    .filter((item) => Number.isFinite(item.count) && item.count > 0)
    .map((item) => [item.navKey, item.count]))
  const badgeDe = (item: NavItem): number => compteurs.get(item.navKey) ?? 0
  // v05x-ux-vague1 (L7, UXA-DC03) : « Décisions à prendre (n) » — n = les étapes que JE peux faire
  // maintenant (`next_step.section = mine`, calculé par la passerelle). Même cache que la file.
  const fileDecisions = useQuery({
    queryKey: ['approvals', workspaceId],
    queryFn: () => api.listApprovals(workspaceId!),
    enabled: Boolean(workspaceId) && niveau === 'projet',
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  })
  const decisionsPourMoi = (fileDecisions.data ?? []).filter((a) => a.next_step?.section === 'mine').length

  const closeOnMobile = (): void => { if (isMobile) setOpenMobile(false) }
  // Toute navigation ferme le tiroir / panneau (y compris depuis « Créer… » et la recherche).
  const routeVue = useRef(`${pathname}${search}`)
  useEffect(() => {
    const route = `${pathname}${search}`
    if (routeVue.current === route) return
    routeVue.current = route
    if (isMobile) setOpenMobile(false)
  }, [pathname, search, isMobile, setOpenMobile])

  /** Entrées d'un groupe : rôle, Découverte (projet), et les entrées masquées en Découverte qui
   *  portent une notification non lue (sinon le badge désignerait une destination introuvable). */
  const entreesDe = (groupe: NavGroup): NavItem[] => {
    const items = itemsDuGroupePourMode(groupe, session.roles, mode)
    if (mode === 'complete') return items
    const avecNotif = groupe.items.filter((i) => !items.includes(i) && !i.horsBarre
      && itemVisiblePourRoles(i, session.roles) && badgeDe(i) > 0)
    return groupe.items.filter((i) => items.includes(i) || avecNotif.includes(i))
  }

  const administre = session.roles.includes('org_admin') || session.roles.includes('admin')
  const lien = (item: NavItem, testid: string, premier = false): JSX.Element => {
    const courant = actif === item
    const libelle = t(`nav.${item.path}`, item.label)
    return (
      <Link to={item.path} onClick={closeOnMobile} data-testid={testid}
            aria-current={courant ? 'page' : undefined}
            title={t(`navdesc.${item.path}`, item.description)}
            className={`${LIEN} ${premier ? 'font-medium' : 'text-[0.8125rem]'}`}>
        <item.icon className="size-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">{libelle}</span>
        {item.etat === '0.5.x' ? (
          <span data-testid={`marque-05x-${item.navKey}`}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-subtle px-2 py-0.5 text-[0.625rem] font-medium text-brand-text">
            <Clock3 className="size-3" aria-hidden="true" />0.5.x
            <span className="sr-only"> — {t('nav.marque05x', MARQUE_0_5_X)}</span>
          </span>
        ) : null}
        {/* v05x-org-neuve : le cadenas dit « réservé à l'administration » à qui CONSULTE sans administrer
            (propriétaire de projet, auditeur). Pour l'administration de l'organisation ou de la plateforme,
            qui y a droit, il se lisait « verrouillé » (parcours réel du 2026-10-05) : il n'est plus montré. */}
        {item.reserveAdministration && !administre ? (
          <>
            <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="sr-only"> — {t('nav.reserveAdministration', 'réservé à l’administration')}</span>
          </>
        ) : null}
        {item.path === '/approvals' && decisionsPourMoi > 0 ? (
          <span data-testid="decisions-a-prendre-compteur"
                className="ml-auto flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-compteur)] px-1.5 text-xs font-semibold tabular-nums text-[var(--color-compteur-text)]">
            <span aria-hidden="true">{decisionsPourMoi > 99 ? '99+' : decisionsPourMoi}</span>
            <span className="sr-only">{t('nav.decisionsPourMoi', '{{n}} décision(s) à prendre par vous', { n: decisionsPourMoi })}</span>
          </span>
        ) : (
          <Badge n={badgeDe(item)} testid={`notifications-badge-${item.navKey}`}
                 libelle={t('nav.itemUnread', '{{n}} notification(s) non lue(s)', { n: badgeDe(item) })} />
        )}
      </Link>
    )
  }

  return (
    <Sidebar titreMobile={telephone ? t('nav.menu', 'Menu') : t(`nav.niveau.${niveau}`, niv.navLabel)}>
      <SidebarHeader>
        {telephone ? (
          <div className="flex flex-col gap-2" data-testid="tiroir-entete">
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setOpenMobile(false)} data-testid="tiroir-fermer"
                      aria-label={t('nav.fermerMenu', 'Fermer le menu')}
                      className="flex size-11 shrink-0 items-center justify-center rounded-md hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring">
                <X className="size-5" aria-hidden="true" />
              </button>
              <p className="text-base font-semibold" aria-hidden="true">{t('nav.menu', 'Menu')}</p>
            </div>
            {onRechercher ? (
              <button type="button" data-testid="tiroir-rechercher"
                      onClick={() => { setOpenMobile(false); onRechercher() }}
                      className="flex min-h-11 w-full items-center gap-2 rounded-md border bg-background px-3 text-left text-sm text-muted-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring">
                <Search className="size-4 shrink-0" aria-hidden="true" />
                {t('nav.rechercherTiroir', 'Rechercher un écran, une action…')}
              </button>
            ) : null}
          </div>
        ) : (
          variante === 'bureau' ? null : <Brand />
        )}
      </SidebarHeader>

      <SidebarContent>
        <nav aria-label={t(`nav.niveau.${niveau}`, niv.navLabel)} data-testid="nav-niveau" data-niveau={niveau}
             onKeyDown={deplacerFocus} className="flex flex-col px-2 pb-2">
          <p data-testid="nav-titre-niveau"
             className="px-2 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">
            {t(`nav.titreNiveau.${niveau}`, niv.label)}
          </p>
          <ul className="flex flex-col gap-0.5">
            <li>{lien(ACCUEIL, 'nav-accueil', true)}</li>
            {premierNiveau(niveau, session.roles, mode).map((groupe) => {
              const entrees = entreesDe(groupe)
              if (entrees.length === 0) return null
              if (groupe.type === 'entree') {
                return <li key={groupe.id} data-testid={`entree-${groupe.id}`}>{lien(entrees[0]!, `lien-${groupe.id}`, true)}</li>
              }
              const ouvert = !estReplie(groupe.id)
              const idListe = `groupe-${niveau}-${groupe.id}`
              const libelle = t(`grp.${groupe.label}`, groupe.label)
              // Notifications d'un groupe replié : jamais perdues de vue.
              const cachees = ouvert ? 0 : entrees.reduce((s, i) => s + badgeDe(i), 0)
              return (
                <li key={groupe.id} data-testid={`groupe-${groupe.id}`} className="pt-1">
                  <button type="button" data-testid={`groupe-bouton-${groupe.id}`}
                          aria-expanded={ouvert} aria-controls={idListe}
                          onClick={() => basculer(groupe.id)}
                          className={`${LIEN} font-semibold`}>
                    <groupe.icon className="size-4 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{libelle}</span>
                    <Badge n={cachees} testid={`groupe-notifications-${groupe.id}`}
                           libelle={t('nav.sectionUnread', '{{n}} notification(s) non lue(s)', { n: cachees })} />
                    <ChevronDown className={`size-4 shrink-0 text-muted-foreground motion-safe:transition-transform ${ouvert ? 'rotate-180' : ''}`}
                                 aria-hidden="true" />
                  </button>
                  <ul id={idListe} hidden={!ouvert} data-testid={`groupe-liste-${groupe.id}`} className="ml-3 border-l pl-2">
                    {blocsParEtape(entrees).flatMap((bloc, rang) => {
                      if (!bloc.etape) return bloc.items.map((item) => <li key={item.path}>{lien(item, `lien-${item.navKey}`)}</li>)
                      const id = idEtape(groupe.id, bloc.etape)
                      const etapeOuverte = !replisEtapes.estReplie(id)
                      const idSousListe = `etape-${niveau}-${groupe.id}-${rang}`
                      const cacheesEtape = etapeOuverte ? 0 : bloc.items.reduce((n, i) => n + badgeDe(i), 0)
                      return [
                        <li key={`etape-${bloc.etape}`} data-testid="etape" data-etape={bloc.etape}>
                          <button type="button" data-testid="etape-bouton"
                                  aria-expanded={etapeOuverte} aria-controls={idSousListe}
                                  onClick={() => replisEtapes.basculer(id)}
                                  className="flex min-h-9 w-full min-w-0 items-center gap-1.5 rounded-md bg-sidebar px-2 text-left text-[0.8125rem] font-semibold text-muted-foreground outline-none hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring">
                            <span data-testid="etape-sous-titre" className="min-w-0 flex-1 py-1 leading-snug [overflow-wrap:anywhere]">{libelleSousTitreEtape(bloc.etape, t)}</span>
                            <Badge n={cacheesEtape} testid={`etape-notifications-${groupe.id}-${rang}`}
                                   libelle={t('nav.sectionUnread', '{{n}} notification(s) non lue(s)', { n: cacheesEtape })} />
                            <ChevronDown className={`size-4 shrink-0 motion-safe:transition-transform ${etapeOuverte ? 'rotate-180' : ''}`}
                                         aria-hidden="true" />
                          </button>
                          <ul id={idSousListe} hidden={!etapeOuverte} data-testid="etape-liste" className="ml-2 border-l pl-2">
                            {bloc.items.map((item) => <li key={item.path}>{lien(item, `lien-${item.navKey}`)}</li>)}
                          </ul>
                        </li>,
                      ]
                    })}
                  </ul>
                </li>
              )
            })}
          </ul>
          {parent ? (
            <Link to={parent.to} onClick={closeOnMobile} data-testid="tiroir-niveau-parent" className={`${LIEN} mt-2 font-medium`}>
              <ArrowUp className="size-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">{t(`nav.parent.${niveau}`, parent.label)}</span>
            </Link>
          ) : null}
        </nav>
        {/* Téléphone (CoquilleTelephone, TRANS-r2-07) : la barre haute ne garde que Menu, contexte,
            Boîte d'attention et Compte ; Notifications, Créer et l'Aide passent au pied du tiroir. */}
        {telephone ? (
          <div role="group" aria-label={t('nav.entreesBarreHaute', 'Entrées de la barre haute')}
               data-testid="tiroir-barre-haute" className="flex flex-col gap-0.5 border-t px-2 py-2">
            <Link to="/notifications" onClick={closeOnMobile} data-testid="tiroir-notifications" className={LIEN}>
              <Bell className="size-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">{t('nav.notifications', 'Notifications')}</span>
              <span className="text-xs text-muted-foreground">
                {t('nav.nonLues', '{{n}} non lue(s)', { n: String(nNonLues) })}
              </span>
            </Link>
            {onNewProject ? <CreateMenu onNewProject={() => { setOpenMobile(false); onNewProject() }} declencheur="tiroir" /> : null}
            <Link to="/guide" onClick={closeOnMobile} data-testid="tiroir-aide" className={LIEN}>
              <CircleHelp className="size-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">{t('nav.aidePriseEnMain', 'Aide et prise en main')}</span>
            </Link>
          </div>
        ) : null}
      </SidebarContent>

      <SidebarFooter>
        {slotPied ? <div data-testid="sidebar-slot-pied" className="px-2 py-1">{slotPied}</div> : null}
        {niveau === 'projet' && onModeChange ? (
          <div role="group" aria-label={t('nav.affichageProjet', 'Affichage de la navigation du projet')}
               data-testid="nav-mode" className="flex gap-1 px-2">
            {(['complete', 'discovery'] as const).map((m) => (
              <button key={m} type="button" aria-pressed={mode === m} data-testid={`nav-mode-${m}`}
                      onClick={() => onModeChange(m)}
                      className={`min-h-10 flex-1 rounded-md border text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring ${
                        mode === m ? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground' : 'text-muted-foreground'}`}>
                {m === 'complete' ? t('nav.modeComplet', 'Complet') : t('nav.modeDecouverte', 'Découverte')}
              </button>
            ))}
          </div>
        ) : null}
        {/* Lot NAV 0.5.x (A2 constat 8) : « Prise en main » n'avait de lien qu'au tiroir téléphone et
            dans une bulle d'aide ; en bureau et tablette, il vit au pied de la barre. */}
        {!telephone ? (
          <Link to="/guide" onClick={closeOnMobile} data-testid="pied-aide" className={`${LIEN} text-[0.8125rem]`}>
            <CircleHelp className="size-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1">{t('nav.aidePriseEnMain', 'Aide et prise en main')}</span>
          </Link>
        ) : null}
        <SelecteurVue />
        <div className="flex items-center justify-between px-2 py-1">
          <p className="min-w-0 flex-1 truncate pr-2 text-xs text-muted-foreground tabular-nums"
             data-testid="app-version"
             title={BUILD_DATE
               ? t('app.versionDeployeeLe', 'Version déployée {{version}} — déployée le {{date}}', { version: APP_VERSION, date: BUILD_DATE })
               : t('app.versionDeployee', 'Version déployée {{version}}', { version: APP_VERSION })}>
            {APP_VERSION}{BUILD_DATE ? ` · ${BUILD_DATE}` : ''}
          </p>
          <div className="flex items-center text-xs" data-testid="lang-switch"
               role="group" aria-label={t('lang.label', 'Langue')}>
            <button type="button" onClick={() => setLang('fr')}
                    className={`flex h-10 min-w-10 items-center justify-center rounded-md ${
                      lang === 'fr' ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
                    aria-pressed={lang === 'fr'}>FR</button>
            <span className="text-muted-foreground/50" aria-hidden="true">/</span>
            <button type="button" onClick={() => setLang('en')} data-testid="lang-en"
                    className={`flex h-10 min-w-10 items-center justify-center rounded-md ${
                      lang === 'en' ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
                    aria-pressed={lang === 'en'}>EN</button>
          </div>
        </div>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
